import * as React from 'react';
import { APPROVED_TENANT_ROLES } from '../../domain/models.js';
import type { TenantFeatureMode, TenantUser, UserAccessState } from '../../domain/models.js';
import { repository } from '../../repositories/index.js';

const stateClass: Record<UserAccessState, string> = {
  active: 'success',
  inactive: 'neutral',
  pending: 'warning',
  rejected: 'danger',
};

interface UsersPageProps {
  mode?: TenantFeatureMode;
}

export function UsersPage({ mode = 'tenant' }: UsersPageProps) {
  const readOnly = mode === 'support-readonly';
  const [users, setUsers] = React.useState<TenantUser[]>([]);
  const [formOpen, setFormOpen] = React.useState(!readOnly);
  const [added, setAdded] = React.useState(false);

  React.useEffect(() => {
    repository.listTenantUsers().then(setUsers);
  }, []);

  React.useEffect(() => {
    if (readOnly) setFormOpen(false);
  }, [readOnly]);

  const reactivate = (id: string) => {
    if (readOnly) return;
    setUsers((current) => current.map((user) => (
      user.id === id
        ? { ...user, state: 'active', stateLabel: 'Активен', updatedAt: '12.08.2026' }
        : user
    )));
  };

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!readOnly) setAdded(true);
  };

  return (
    <div className="page" data-testid="users-page" data-read-only={readOnly ? 'true' : 'false'}>
      <header className="page-header with-actions">
        <div>
          <p className="eyebrow">ТЕНАНТ · ДОСТУП</p>
          <h1>Пользователи и доступ</h1>
          <p>Одна утверждённая роль на пользователя в тенанте. Сервер остаётся источником авторизации.</p>
        </div>
        {readOnly
          ? <span className="readonly-chip">Только чтение</span>
          : <button type="button" className="button primary" data-mutation-control="add-user" onClick={() => setFormOpen((value) => !value)}>Добавить сотрудника</button>}
      </header>
      <div className={formOpen ? 'users-layout form-visible' : 'users-layout'}>
        <section className="panel table-panel">
          <div className="data-table users-table" role="table">
            <div className="data-row data-header"><span>Пользователь</span><span>Роль</span><span>Состояние</span><span>MFA</span><span>Изменено</span><span /></div>
            {users.map((user) => (
              <div className="data-row" key={user.id} data-user-state={user.state} data-tenant-role={user.role} data-testid={user.state === 'pending' ? 'pending-user' : undefined}>
                <span><strong>{user.name}</strong><small>{user.email}</small></span>
                <span className="tenant-role-value">{user.role}</span>
                <span className={`access-state state-${stateClass[user.state]}`}>{user.stateLabel}</span>
                <span>{user.mfa}</span><span>{user.updatedAt}</span>
                {readOnly
                  ? <span className="readonly-row-label">Только чтение</span>
                  : user.state === 'inactive'
                    ? <button type="button" className="button small secondary" data-mutation-control="reactivate-user" onClick={() => reactivate(user.id)}>Активировать</button>
                    : <button type="button" className="button small ghost" data-mutation-control="edit-user">Изменить</button>}
              </div>
            ))}
          </div>
        </section>
        {!readOnly && formOpen ? (
          <aside className="panel employee-form-panel">
            <h2>Добавить сотрудника</h2>
            <p>Для уже одобренной личности доступ можно активировать напрямую. Новая личность будет ждать одобрения DataSota.</p>
            <form onSubmit={submit}>
              <label>Имя<input required placeholder="Имя и фамилия" /></label>
              <label>Email<input required type="email" placeholder="name@company.ru" /></label>
              <label>
                Роль
                <select defaultValue="Ответственный за сопоставления" data-testid="tenant-role-select">
                  {APPROVED_TENANT_ROLES.map((role) => <option key={role}>{role}</option>)}
                </select>
              </label>
              <button type="submit" className="button primary full-width" data-mutation-control="submit-user">Отправить на подключение</button>
            </form>
            {added ? <div className="inline-success" role="status">Mock-заявка создана. Пользователь показан как ожидающий одобрения, не активный.</div> : null}
          </aside>
        ) : null}
      </div>
    </div>
  );
}
